/**
 * @typedef {Object} FieldError
 * @property {string} field - The field name that failed validation
 * @property {string} code - The error code for this field
 * @property {string} message - The error message for this field
 */

/**
 * @typedef {Object} ErrorDetails
 * @property {FieldError[]} [fields] - Array of field validation errors (for VALIDATION errors)
 * @property {string} [requiredPrivilege] - Required privilege name (for AUTH_PERMISSION_DENIED)
 * @property {string} [resourceType] - Type of resource (for DATA_DUPLICATE_ENTRY)
 * @property {string} [identifier] - Identifier value (for DATA_DUPLICATE_ENTRY)
 */

/**
 * @typedef {Object} ErrorResponse
 * @property {string} code - Error code constant (e.g., 'VALIDATION_REQUIRED_FIELD')
 * @property {string} message - Human-readable error message
 * @property {ErrorDetails|null} details - Additional error details (structure varies by error type)
 * @property {string} timestamp - ISO 8601 timestamp
 * @property {string} errorId - 8-character UUID prefix for support reference
 * @property {number} [httpStatus] - HTTP status code (only for legacy responses)
 */

// ============================================================
// AUTH ERROR CODES
// ============================================================
export const AUTH_PERMISSION_DENIED = 'AUTH_PERMISSION_DENIED';
export const AUTH_SESSION_EXPIRED = 'AUTH_SESSION_EXPIRED';
export const AUTH_INVALID_CREDENTIALS = 'AUTH_INVALID_CREDENTIALS';
export const AUTH_ACCOUNT_LOCKED = 'AUTH_ACCOUNT_LOCKED';

// ============================================================
// VALIDATION ERROR CODES
// ============================================================
export const VALIDATION_REQUIRED_FIELD = 'VALIDATION_REQUIRED_FIELD';
export const VALIDATION_INVALID_FORMAT = 'VALIDATION_INVALID_FORMAT';
export const VALIDATION_INVALID_VALUE = 'VALIDATION_INVALID_VALUE';
export const VALIDATION_TOO_LONG = 'VALIDATION_TOO_LONG';
export const VALIDATION_TOO_SHORT = 'VALIDATION_TOO_SHORT';
export const VALIDATION_EMAIL_INVALID = 'VALIDATION_EMAIL_INVALID';
export const VALIDATION_OUT_OF_RANGE = 'VALIDATION_OUT_OF_RANGE';
export const VALIDATION_INVALID_DATE = 'VALIDATION_INVALID_DATE';

// ============================================================
// DATA ERROR CODES
// ============================================================
export const DATA_NOT_FOUND = 'DATA_NOT_FOUND';
export const DATA_DUPLICATE_ENTRY = 'DATA_DUPLICATE_ENTRY';
export const DATA_CONSTRAINT_VIOLATION = 'DATA_CONSTRAINT_VIOLATION';
export const DATA_INTEGRITY_ERROR = 'DATA_INTEGRITY_ERROR';
export const DATA_STALE_REFERENCE = 'DATA_STALE_REFERENCE';

// ============================================================
// FILE ERROR CODES
// ============================================================
export const FILE_INVALID_TYPE = 'FILE_INVALID_TYPE';
export const FILE_TOO_LARGE = 'FILE_TOO_LARGE';
export const FILE_UPLOAD_FAILED = 'FILE_UPLOAD_FAILED';
export const FILE_CORRUPTED = 'FILE_CORRUPTED';
export const FILE_EMPTY = 'FILE_EMPTY';
export const FILE_PARSE_ERROR = 'FILE_PARSE_ERROR';

// ============================================================
// SERVER ERROR CODES
// ============================================================
export const SERVER_INTERNAL_ERROR = 'SERVER_INTERNAL_ERROR';
export const SERVER_DATABASE_ERROR = 'SERVER_DATABASE_ERROR';
export const SERVER_UNAVAILABLE = 'SERVER_UNAVAILABLE';
export const SERVER_TIMEOUT = 'SERVER_TIMEOUT';

// ============================================================
// ACTION ERROR CODES
// ============================================================
export const ACTION_UNSUPPORTED = 'ACTION_UNSUPPORTED';
export const ACTION_FAILED = 'ACTION_FAILED';
export const ACTION_INVALID_STATE = 'ACTION_INVALID_STATE';
export const ACTION_INVALID_PARAMS = 'ACTION_INVALID_PARAMS';

// ============================================================
// CATEGORY CHECK FUNCTIONS
// ============================================================

/**
 * Check if error code is an authentication error
 * @param {string|null} code - Error code to check
 * @returns {boolean} True if auth error
 */
export function isAuthError(code) {
  if (!code) return false;
  return code.startsWith('AUTH_');
}

/**
 * Check if error code is a validation error
 * @param {string|null} code - Error code to check
 * @returns {boolean} True if validation error
 */
export function isValidationError(code) {
  if (!code) return false;
  return code.startsWith('VALIDATION_');
}

/**
 * Check if error code is a data error
 * @param {string|null} code - Error code to check
 * @returns {boolean} True if data error
 */
export function isDataError(code) {
  if (!code) return false;
  return code.startsWith('DATA_');
}

/**
 * Check if error code is a file error
 * @param {string|null} code - Error code to check
 * @returns {boolean} True if file error
 */
export function isFileError(code) {
  if (!code) return false;
  return code.startsWith('FILE_');
}

/**
 * Check if error code is a server error
 * @param {string|null} code - Error code to check
 * @returns {boolean} True if server error
 */
export function isServerError(code) {
  if (!code) return false;
  return code.startsWith('SERVER_');
}

/**
 * Check if error code is an action error
 * @param {string|null} code - Error code to check
 * @returns {boolean} True if action error
 */
export function isActionError(code) {
  if (!code) return false;
  return code.startsWith('ACTION_');
}

/**
 * Check if error code is a permission error specifically
 * @param {string|null} code - Error code to check
 * @returns {boolean} True if permission denied error
 */
export function isPermissionError(code) {
  return code === AUTH_PERMISSION_DENIED;
}

/**
 * Check if error code is a duplicate entry error
 * @param {string|null} code - Error code to check
 * @returns {boolean} True if duplicate entry error
 */
export function isDuplicateError(code) {
  return code === DATA_DUPLICATE_ENTRY;
}
